import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getItem, deleteItem, InventoryItem, StockLedgerEntry } from './api';
import './inventory-v2.css';
import DataTable from '../../shared/components/DataTable';

const ItemDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { sessionToken, user } = useAuth();
  const navigate = useNavigate();
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [receivingHistory, setReceivingHistory] = useState<any[]>([]);
  const [ledger, setLedger] = useState<StockLedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    getItem(sessionToken, parseInt(id))
      .then(data => {
        setItem(data.item);
        setReceivingHistory(data.receiving_history);
        setLedger(data.stock_ledger);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [sessionToken, id]);

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete/deactivate this item?')) return;
    try {
      await deleteItem(sessionToken, parseInt(id));
      navigate('/inventory/items');
    } catch (e: any) {
      setError(e.message);
    }
  };

  if (loading) return <div className="inv2-loading">Loading...</div>;
  if (error) return <div className="inv2-error">{error}</div>;
  if (!item) return <div className="inv2-error">Item not found</div>;

  const isOwner = user?.role === 'Owner';

  return (
    <div className="inv2-page">
      <div className="inv2-header">
        <div>
          <Link to="/inventory/items" className="inv2-back-link">← Back to Items</Link>
          <h1>{item.name} {item.brand && <span className="inv2-brand">({item.brand})</span>}</h1>
          <span className={`inv2-status inv2-status-${item.status}`}>{item.stock_known===false?'Count needed':item.status}</span>
          {!item.is_active && <span className="inv2-badge-inactive">Inactive</span>}
        </div>
        <div className="inv2-header-actions">
          {['Owner', 'Admin', 'Manager'].includes(user?.role || '') && (
            <>
              <Link to={`/inventory/items/${id}/edit`} className="inv2-btn">Edit</Link>
              {isOwner && <button onClick={handleDelete} className="inv2-btn inv2-btn-danger">Delete</button>}
            </>
          )}
        </div>
      </div>

      {/* Item Details */}
      <div className="inv2-detail-grid">
        <div className="inv2-detail-section">
          <h3>General Information</h3>
          <dl className="inv2-dl">
            <dt>Supplier</dt><dd>{item.supplier_name || '—'}</dd>
            <dt>Category</dt><dd>{item.category_name || '—'}</dd>
            <dt>Base Unit</dt><dd>{item.base_unit}</dd>
            <dt>Purchase Unit</dt><dd>{item.purchase_unit || '—'}</dd>
            <dt>Purchase Quantity</dt><dd>{item.purchase_quantity ?? '—'}</dd>
            <dt>Purchase Cost</dt><dd>{item.purchase_cost != null ? `$${Number(item.purchase_cost).toFixed(2)}` : '—'}</dd>
            <dt>Notes</dt><dd>{item.notes || '—'}</dd>
          </dl>
        </div>

        <div className="inv2-detail-section">
          <h3>Stock Levels</h3>
          <dl className="inv2-dl">
            <dt>Current Stock</dt><dd className="inv2-big-value">{item.stock_known===false?'Not counted':`${Number(item.on_hand_quantity).toFixed(1)} ${item.base_unit}`}</dd>
            <dt>Low Stock Threshold</dt><dd>{item.threshold_quantity ?? 'Not set'}</dd>
            <dt>Par Level (Target)</dt><dd>{item.par_level ?? 'Not set'}</dd>
            <dt>Status</dt><dd><span className={`inv2-status inv2-status-${item.status}`}>{item.stock_known===false?'Count needed':item.status}</span></dd>
          </dl>
        </div>

        <div className="inv2-detail-section">
          <h3>Pricing</h3>
          <dl className="inv2-dl">
            <dt>Last Purchase Price</dt>
            <dd>{item.last_received_unit_price != null ? `$${Number(item.last_received_unit_price).toFixed(4)} / ${item.base_unit}` : '—'}
              {item.price_manually_overridden && <span className="inv2-badge-info">Manual</span>}
            </dd>
            <dt>Average Purchase Price</dt>
            <dd>{item.average_unit_price != null ? `$${Number(item.average_unit_price).toFixed(4)} / ${item.base_unit}` : '—'}</dd>
            <dt>Weighted Avg Price</dt>
            <dd>{item.weighted_average_unit_price != null ? `$${Number(item.weighted_average_unit_price).toFixed(4)} / ${item.base_unit}` : '—'}</dd>
            <dt>Last Received</dt>
            <dd>{item.last_received_at ? new Date(item.last_received_at).toLocaleDateString() : 'Never'}</dd>
          </dl>
        </div>
      </div>

      {/* Receiving History */}
      {receivingHistory.length > 0 && (
        <div className="inv2-section">
          <h2>📦 Receiving History</h2>
          <div className="inv2-table-container">
            <DataTable
              rows={receivingHistory}
              rowKey={(_r, i) => i}
              caption="Receiving history"
              empty="No receiving history"
              columns={[
                { key: 'invoice', header: 'Invoice', priority: 'primary', render: (r) => r.invoice_number || '—' },
                { key: 'qty', header: 'Quantity', priority: 'summary', numeric: true, render: (r) => `${Number(r.quantity_received).toFixed(1)} ${r.unit}` },
                { key: 'total', header: 'Total Cost', priority: 'summary', numeric: true, render: (r) => `$${Number(r.purchase_cost).toFixed(2)}` },
                { key: 'date', header: 'Date', priority: 'summary', render: (r) => (r.invoice_date ? new Date(r.invoice_date).toLocaleDateString() : '—') },
                { key: 'unit', header: 'Unit Price', priority: 'detail', numeric: true, render: (r) => `$${Number(r.unit_price).toFixed(4)}` },
              ]}
            />
          </div>
        </div>
      )}

      {/* Stock Ledger */}
      {ledger.length > 0 && (
        <div className="inv2-section">
          <h2>📋 Recent Stock Movements</h2>
          <div className="inv2-table-container">
            <DataTable
              rows={ledger}
              rowKey={(entry) => entry.id}
              caption="Stock movements"
              empty="No movements"
              columns={[
                { key: 'type', header: 'Type', priority: 'primary', render: (entry) => <span className="inv2-movement-type">{entry.movement_type.replace(/_/g, ' ')}</span> },
                {
                  key: 'qty', header: 'Quantity', priority: 'summary', numeric: true,
                  render: (entry) => (
                    <span className={Number(entry.quantity_delta) > 0 ? 'inv2-text-success' : 'inv2-text-danger'}>
                      {Number(entry.quantity_delta) > 0 ? '+' : ''}{Number(entry.quantity_delta).toFixed(1)} {entry.unit}
                    </span>
                  ),
                },
                { key: 'date', header: 'Date', priority: 'summary', render: (entry) => new Date(entry.created_at).toLocaleString() },
                { key: 'notes', header: 'Notes', priority: 'summary', render: (entry) => entry.notes || '—' },
              ]}
            />
          </div>
          <Link to={`/inventory/ledger?item_id=${id}`} className="inv2-link">View full ledger →</Link>
        </div>
      )}
    </div>
  );
};

export default ItemDetail;
